import {Box, BoxFillIcon, FullscreenPanel, FullscreenPanelBody, Paper, Pvz2FillIcon} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useUpdateEffect} from '@yandex-delivery/utils/hooks'
import {type ReactElement, memo, useMemo, useState} from 'react'
import {useController, useFormContext, useWatch} from 'react-hook-form'

import {useResolvePickupPoint} from '../../api/address/services/pickupPointService/pickupPoint'
import {renderWidget} from '..'
import type {AddressWidgetProps} from '../types'
import {PointModal} from './PointModal/PointModal'
import {PointSlot} from './PointSlot/PointSlot'
import type {PickupTarget} from './types'
import {createNames} from './utils'

type Props = {
  config: AddressWidgetProps['config']
  name: string
}
type State = {
  target: PickupTarget
  opened: boolean
}

export const PickupRoute = memo<Props>(function PickupRoute({name, config}: Props): ReactElement | null {
  const i18n = useI18n()
  const {watch, setValue, control} = useFormContext()
  const [state, setState] = useState<State>({
    target: 'source',
    opened: false,
  })

  const SlotTrail = useMemo(
    () => (
      <Box alignItems="center" gap="xs">
        {config.trail && renderWidget(config.trail)}
        {config.trail2 && renderWidget(config.trail2)}
      </Box>
    ),
    [config.trail, config.trail2],
  )

  const isSource = config.route_point_index === 0
  const target = isSource ? 'source' : 'destination'

  useController({
    control,
    name: createNames(`${name}.${target}`).pickupPoint,
  })

  useResolvePickupPoint(`${name}.${target}`, isSource)

  const isBusiness: boolean = useWatch({name: 'is_business'})

  useUpdateEffect(() => {
    if (isSource) {
      setValue(`${name}.source.pickupPoint`, undefined)
    }
  }, [isBusiness])

  if (config.route_point_index > 1) {
    return null
  }

  const isDisabledPointTypeDestination = !isSource && watch('ndd.route.source.pickupPoint') === undefined

  return (
    <Paper mt={undefined} item>
      <Paper mt={undefined} item>
        <PointSlot
          key={target}
          isDestination={!isSource}
          lead={isSource ? <BoxFillIcon /> : <Pvz2FillIcon />}
          name={`${name}.${target}`}
          onPress={() => {
            if (isDisabledPointTypeDestination) {
              return
            }
            setState({opened: true, target})
          }}
          text={
            isSource
              ? i18n('bdui.pickup_points.placeholder.send', 'Отправить')
              : i18n('bdui.pickup_points.placeholder.receive', 'Доставить')
          }
          trail={SlotTrail}
        />
      </Paper>
      <FullscreenPanel anchor="bottom" color="minor" opened={state.opened}>
        <FullscreenPanelBody>
          <PointModal
            key={state.target}
            name={`${name}.${state.target}`}
            onClose={() => {
              setState({target: state.target, opened: false})
            }}
            onContinue={() => {
              setState({target: 'destination', opened: state.target === 'source'})
            }}
            target={state.target}
          />
        </FullscreenPanelBody>
      </FullscreenPanel>
    </Paper>
  )
})
